/* eslint-disable @typescript-eslint/camelcase */
import React, { useState, useEffect } from 'react';
import Helmet from 'react-helmet';
import { getAppEnv } from '../../../config/env';
import DataLoaderError from '../../../server/errors/DataLoaderError';
import BreadCrumb from '../../components/BreadCrumb/BreadCrumb';
import CabModifySearch from '../../components/CabModifySearch/CabModifySearch';
import PageHeader from '../../components/PageHeader/PageHeader';
import { OUTSTATION_ONE_WAY } from '../../constants/tripTypes';
import { EnvironmentVariables } from '../../types';
import { useMediaQuery } from 'react-responsive';
import './RydeHomePage.css';
import Footer from '../../components/Footer/Footer';
import FaqSection from '../../components/Faq/Faq';
import CompactList from '../../components/CompactList/CompactList';
import '../CityOperatorTemplate/CityOperatorTemplate.css';
import AdsComponent from '../../components/adsComponent';
import '../../components/ReadMore/ReadMore.css';
import { ThrowReporter } from 'io-ts/lib/ThrowReporter';
import { PathReporter } from 'io-ts/lib/PathReporter';
import AppContextProvider from '../../AppContext';
import { trackRydeHomePageCabPageEvent } from '../../utils/omnitureTracking';
import RentalCabs from '../../components/RentalCabs/RentalCabs';
import '../../components/TopCabsRoutes/TopCabsRoutes.css';
import RydeAssuredCard from '../../components/RydeAssuredCard/RydeAssuredCard';
import {
  RydeHomePageData,
  RydeHomePageDataChecker
} from './RydeHomePageDataChecker';
import ThingsToKnow from '../../components/ThingsToKnow/ThingsToKnow';
import TopCabsRoutes from '../../components/TopCabsRoutes/TopCabsRoutes';
import { SEO_RYDE_HOME_PAGE } from '../../constants/templateConstants';
import { useSearchWidgetHooks } from '../../utils/commonUtils';

const env = getAppEnv();
const { DATA_LOADER_URL } = env.raw as EnvironmentVariables;

type RydeHomePageDataProp = {
  data: RydeHomePageData;
};

const runtimeTypChecker = (data: RydeHomePageData) => {
  const result = RydeHomePageDataChecker.decode(data);
  if (result._tag !== 'Right') {
    // console.log(PathReporter.report(result));
    ThrowReporter.report(result);
  }
};

const RydeHomePage = ({ data }: RydeHomePageDataProp) => {
  //   runtimeTypChecker(data);

  const isMobileDevice = useMediaQuery({
    query: '(max-device-width: 768px)'
  });
  const { travelFor, pickUpTime, dropTime } = useSearchWidgetHooks(
    SEO_RYDE_HOME_PAGE
  );

  const { templateData, cabTypesAvailableForRent, hrPackages } = data;

  return (
    <AppContextProvider
      omniUrlArgs={`source=${SEO_RYDE_HOME_PAGE}`}
      trackTemplateEvent={trackRydeHomePageCabPageEvent}
    >
      <div className={`cabSeoDtPage`}>
        <Helmet>
          <title>{templateData.metaTitle}</title>
          <meta name="description" content={templateData.metaDescription} />
          <meta name="keywords" content={templateData.metaKeywords} />
          <link rel="canonical" href={templateData.sampleUrl} />
        </Helmet>
        <div className="infoForCrawler">
          <BreadCrumb breadcrumb={templateData.breadcrumb} />
          <PageHeader
            title={templateData.h1Title}
            extraInfo={templateData.extraInfo}
            isMobileDevice={isMobileDevice}
            isExtraInfoVisible={true}
          />
        </div>
        <div className={`mobileShow makeSticky`}>
          <CabModifySearch
            pickupTime={pickUpTime}
            defaultTripType={travelFor}
            returnTime={dropTime}
            isMobileDevice={true}
            title={`Ryde Cabs`}
            omniString={`_Ryde_Home_Page`}
            isRydeHomePage={true}
            hrPackages={hrPackages}
          />
        </div>

        <div className={`desktopShow makeSticky`}>
          <CabModifySearch
            pickupTime={pickUpTime}
            defaultTripType={travelFor}
            returnTime={dropTime}
            returnCloseFlagDefault={false}
            isMobileDevice={false}
            title={`Ryde Cabs`}
            omniString={`_Ryde_Home_Page`}
            isRydeHomePage={true}
            hrPackages={hrPackages}
          />
        </div>
        <main className="container">
          <div style={{ display: 'none' }} className={'infoForUser'}>
            <BreadCrumb breadcrumb={templateData.breadcrumb} />
            <PageHeader
              title={templateData.h1Title}
              extraInfo={templateData.extraInfo}
              isMobileDevice={isMobileDevice}
              isExtraInfoVisible={true}
            />
          </div>
          <div className="appendBottom20">
            {
              <AdsComponent
                template={'City'}
                placedAt={'top'}
                isMobileDevice={isMobileDevice}
                desktopAdConfig={{
                  position: templateData.bannerDesktop
                    ? templateData.bannerDesktop
                    : 'none',
                  class: 'desktopShow appendBottom20 adsLayout',
                  style: {}
                }}
                mobileAdConfig={{
                  position: templateData.bannerMobile
                    ? templateData.bannerMobile
                    : 'none',
                  class: 'mobileShow appendBottom20 adsLayout',
                  style: { marginTop: null }
                }}
              />
            }
          </div>
          <div className="rapBg makeFlex column appendBottom20">
            <RydeAssuredCard benefits={data.rydeUsps} />
          </div>
          {data.topRydeRoutes.cabTypes.length > 0 && (
            <TopCabsRoutes
              h2={`Top Ryde Cabs Routes`}
              cityName={'city'}
              routeType="From"
              data={data.topRydeRoutes}
              enableAnchorTags={true}
              isRydeHomePage={true}
              rydeEnabled={true}
            />
          )}
          <RentalCabs
            h2={`Ryde Cab Prices`}
            cityName={''}
            data={cabTypesAvailableForRent}
          />
          <div
            style={{ flexWrap: 'wrap' }}
            className={`cabsSeoWrap__Details makeFlex visibilityAuto`}
          >
            {data.popularRoutesforRydeCities.map((itemM, idx) => (
              <CompactList
                titlePrefix={`Ryde Cabs from `}
                titleSuffix={itemM.sourceCity}
                list={itemM.routes}
                type={'bullets'}
                className={'rightMargin'}
              >
                {(item: any, idx, style) => (
                  <li
                    key={`${item.sourceCityName}_${idx}`}
                    className={`${
                      idx ? `paddingT10 paddingB10` : `paddingB10`
                    }`}
                  >
                    {item.url ? (
                      <a
                        className={`linkBoxFont`}
                        onClick={event => {
                          event.preventDefault();
                          trackRydeHomePageCabPageEvent(
                            `Cab_SEO_Ryde_Home_Page_Route_Link_Click`
                          );
                          window.location = Object(item.url);
                        }}
                        href={item.url}
                      >
                        {`Ryde Cabs from ${item.sourceCityName} to ${item.destinationCityName}`}
                      </a>
                    ) : null}
                  </li>
                )}
              </CompactList>
            ))}
            <CompactList
              titlePrefix={`Popular routes in India`}
              titleSuffix={``}
              list={data.popularRoutes}
              type={'bullets'}
              className={'rightMargin'}
            >
              {(item: any, idx, style) => (
                <li
                  key={`${item.sourceCityName}_${idx}`}
                  className={`${idx ? `paddingT10 paddingB10` : `paddingB10`}`}
                >
                  {item.url ? (
                    <a
                      className={`linkBoxFont`}
                      onClick={event => {
                        event.preventDefault();
                        trackRydeHomePageCabPageEvent(
                          `Cab_SEO_Ryde_Home_Page_Route_Link_Click`
                        );
                        window.location = Object(item.url);
                      }}
                      href={item.url}
                    >
                      {`${item.sourceCityName} to ${item.destinationCityName} Cabs`}
                    </a>
                  ) : null}
                </li>
              )}
            </CompactList>
          </div>
          {templateData.descriptionInHtml ? (
            <section id="descriptionInHtml" className="visibilityAuto">
              <ThingsToKnow html={templateData.descriptionInHtml} />
            </section>
          ) : null}
          <section className="visibilityAuto">
            <h2>{`FAQs on Ryde Cab Rental Service`}</h2>
            <FaqSection
              trackTemplateEvent={trackRydeHomePageCabPageEvent}
              data={templateData.faq}
              pageUrl={templateData.sampleUrl}
            />
          </section>
        </main>
        <>
          <Footer template={data.templateData} />
        </>
      </div>
    </AppContextProvider>
  );
};

RydeHomePage.fetchData = async (_, match, urlObj, gRPCClient) => {
  const urlString = `${DATA_LOADER_URL}/api/getRydeHomePageData?${urlObj.query}`;

  let responseData;
  const response = await fetch(urlString);
  const textResponse = await response.text();
  try {
    responseData = JSON.parse(textResponse);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('url', urlString);
    throw new Error('JSON parse exception: ' + textResponse);
  }
  if (responseData.status !== 'SUCCESS') {
    // eslint-disable-next-line no-console
    console.error('url', urlString);
    throw new DataLoaderError(`Unable to fetch bus list`, responseData.error);
  }

  const pathArray = urlObj.path.split('/');
  const cacheKey =
    'HTML_STATIC_' + pathArray[pathArray.length - 1].toUpperCase();

  // needs to be  in  a separate file or done differently,
  const templateData = {
    templateName: 'static-ryde-cab',
    metaTitle: 'Ryde Cab: Book Ryde Cab online at Lowest Fare - MakeMyTrip',
    metaDescription:
      'Book Ryde cabs online without any hassle and get best price with a variety of vehicles available 24x7, live GPS tracking, superior sanitization and Travel & Safety essentials for your comfortable travel on ryde cab booking',
    metaKeywords:
      'Ryde Cab Services, Book Ryde Cab, Ryde Taxi, ryde cab booking, ryde cab india,ryde cab booking, ryde car booking, ryde taxi booking, ryde cab online booking',
    analytics: `var s=s_gi('mmtprod');
              s.pageName="seo:domestic cabs:RydeHome Cab";
              s.channel="seotemplate domestic cabs funnel";
              s.server = window.location.hostname;
              s.prop24="domestic cabs";
              if(typeof(mboxLoadSCPlugin) != "undefined" ){
              mboxLoadSCPlugin(s);
              }
              var s_code=s.t();if(s_code)document.write(s_code);`,
    h1Title: 'Ryde Cab',
    extraInfo: '',
    bannerMobile: 'top',
    bannerDesktop: 'top',
    breadcrumb: [
      {
        displayString: 'Home',
        redirectionUrl: 'https://www.makemytrip.com/'
      },
      {
        displayString: 'Cabs',
        redirectionUrl: 'https://www.makemytrip.com/cabs/'
      },
      {
        displayString: ' Ryde Cab Booking',
        redirectionUrl: 'https://www.makemytrip.com/car-rental/ryde-cab.html'
      }
    ],
    faq: [
      {
        question: `Will I need to carry any ID for my Ryde cab boarding?`,
        answer: `Yes, you will need to carry any photo identity proof when boarding for your driver to recognize you.`
      },
      {
        question: `Which Ryde booking will be the cheapest?`,
        answer: `The cost of your cab booking depends on the type of vehicle selected, the distance you need to cover, and hours of journey involved. You can choose a set hour-km combination when booking your journey as per the price available and your travelling requirement.`
      },
      {
        question: `What vehicles can I choose from for Ryde cab bookings with Make My Trip?`,
        answer: `You can choose from a range of car models to commence your travel from available vehicle types, which include Sedan, Hatchback, SUV, Prime SUV, and Prime Sedan. Some common models available include Ertiga, Indica, Etios, Amaze, and Innova Crysta.`
      },
      {
        question: `Can I book a round trip with these cabs?`,
        answer: `Yes, booking a round trip is possible with Ryde cabs. When making the booking, choose ‘Outstation Round Trip’ as the trip type to continue.`
      },
      {
        question: `What should I do when I need to go beyond the selected distance?`,
        answer: `When travelling beyond the initially selected distance, you need to pay a nominal per kilometer charge applicable as per the city of travel.`
      }
    ],
    externalJs: '',
    sampleUrl: 'https://www.makemytrip.com/car-rental/ryde-cab.html',
    footerData: responseData.footerData,
    descriptionInHtml: `<h2>About Ryde Cabs</h2>
<p>If you are looking for a cab service that provides you with the right car model as well as suitable seating capacity, a secure journey, and upscale amenities at reasonable rates, then you should book&nbsp;Ryde Cab Services.&nbsp;</p>
<p>Ryde cabs are&nbsp;available on MakeMyTrip across several cities in India. Whether you need to book your ride in advance or make last-minute travel plans,&nbsp;Ryde cab booking&nbsp;can be made without any hassle.&nbsp;<br><br>Cab options such as SUV and Sedan are available to keep your rides comfortable while available models such as Xylo, Ertiga, and Innova spoil you for choices when you&nbsp;Book Ryde Cab&nbsp;next.&nbsp;</p>
<p>Choose your&nbsp;Ryde Taxi&nbsp;online and get a pickup and drop at your desired location within the city or even outstation to suit your travel plans. You can also choose to return in the same Ryde cab after making your&nbsp;car booking&nbsp;and enjoy great savings on the total travel cost.&nbsp;</p>
<p>Make your next&nbsp;online cab booking&nbsp;with Ryde to explore a host of other features up for grabs and make your travel convenient and enjoyable.</p>
<h2>Why Choose Ryde Cabs</h2>
<p style="margin-bottom: 10px">Ryde Cab India&nbsp;on MakeMyTrip brings a handful of reasons that make it your go-to travel partner for your next&nbsp;online taxi booking. Here are some of them:</p>
<div style="margin-left: 15px">
<h2 style="margin-top: 10px">Perfectly Professional Rides</h2>
<p style="margin-bottom: 10px">Your&nbsp;online taxi booking&nbsp;with&nbsp;Ryde cabs&nbsp;on MakeMyTrip assures a professional journey with verified drivers accompanying you on each ride. All drivers are courteous as well as trained to make your trip pleasant and memorable. Moreover, these well-uniformed drivers are verified and licensed.</p>
<h2>Live GPS tracking</h2>
<p style="margin-bottom: 10px">Keep your loved ones updated when traveling in the Ryde Cab with a live GPS tracking system enabled for each trip that you take.</p>
<h2>Superior Sanitisation</h2>
<p style="margin-bottom: 10px">Each&nbsp;cab booking&nbsp;comes with well-sanitized interiors that ensure super hygienic trips as per new precautionary norms in view of the COVID-19 safety.</p>
<h2>Groove-Worthy Sound System</h2>
<p>Keep your Ryde trips enjoyable with a super sound system in each cab and top-quality music to choose from.</p>
</div>
<h2>Ryde Cabs in India</h2>
<p>Whether you are looking for&nbsp;Ryde cab Delhi&nbsp;or&nbsp;Ryde cab Bangalore, or any other city, MakeMyTrip has got you covered. You can make your&nbsp;Ryde car booking&nbsp;in a number of Indian cities including Jaipur, Delhi, Bangalore, Agra, Kapurthala, Shimla, Manali, Karnal, Udaipur, Sonipat, Pune, Mumbai, Ahmedabad, and many more.&nbsp;</p>
<h2>Types of Ryde Cabs</h2>
<p>While making your&nbsp;Ryde cab online booking,&nbsp;you can find two cab types on MakeMyTrip:</p>
<ul>
    <li>
        <p>Sedan</p>
    </li>
    <li>
        <p>SUV</p>
    </li>
</ul>
<p>The car models available under these cab types can vary from one city to another. Some common cars you can find for&nbsp;Ryde taxi booking&nbsp;include the likes of Innova, Ertiga, Dzire, and Etios.&nbsp;</p>
<p>The&nbsp;Ryde Cab Services&nbsp;on MakeMyTrip are immensely affordable and make your travel both enjoyable and cost-effective. The cab fares are set reasonably with extra fare chargeable only after exceeding the agreed distance, which is set nominally at per hour rates as well. The billings include a clear break-up of ride charges and taxes payable with no hidden levy.&nbsp;</p>
<p>Book your next trip with Ryde Cab India today at affordable rates and enjoy a smooth journey.</p>`
  };

  return {
    pushToCache: true, // if this is set then after ssr it will be stored in cache
    cacheKey,
    ...responseData,
    templateData: templateData
  };
};
export default RydeHomePage;
